import React from 'react';
import { useWeather } from '../lib/useWeather';
import CondoLayout from './CondoLayout';

const OUTDATED_AFTER_MS = 15 * 60 * 1000;

const COMPASS_POINTS = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];

const UV_LEVELS = [
  { max: 3, label: 'Low', className: 'bg-priorityLow/20 text-priorityLow' },
  { max: 6, label: 'Moderate', className: 'bg-priorityMedium/20 text-priorityMedium' },
  { max: 8, label: 'High', className: 'bg-priorityHigh/20 text-priorityHigh' },
  { max: Infinity, label: 'Very high', className: 'bg-priorityUrgent/20 text-priorityUrgent' }
];

export function compassPoint(degrees) {
  return COMPASS_POINTS[Math.round((((degrees % 360) + 360) % 360) / 22.5) % 16];
}

export function uvLevel(uvi) {
  return UV_LEVELS.find((level) => uvi < level.max);
}

export function formatAge(observedAt, now = Date.now()) {
  if (!observedAt) return null;
  const minutes = Math.max(0, Math.round((now - new Date(observedAt).getTime()) / 60000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours} h ago`;
}

const show = (value, digits = 0) => (value === null || value === undefined ? '–' : value.toFixed(digits));

function Card({ title, children }) {
  return (
    <div className="bg-card rounded-lg border border-border p-5">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">{title}</h2>
      {children}
    </div>
  );
}

function Stat({ label, value, unit }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold text-foreground">
        {value}
        {unit && <span className="text-sm font-normal text-muted-foreground"> {unit}</span>}
      </p>
    </div>
  );
}

function Compass({ direction }) {
  return (
    <svg viewBox="0 0 100 100" className="w-28 h-28 text-border shrink-0" role="img" aria-label="Wind direction">
      <circle cx="50" cy="50" r="46" fill="none" stroke="currentColor" strokeWidth="2" />
      <g className="fill-current text-muted-foreground text-xs" textAnchor="middle">
        <text x="50" y="16">N</text>
        <text x="86" y="53">E</text>
        <text x="50" y="94">S</text>
        <text x="14" y="53">W</text>
      </g>
      {direction !== null && (
        <polygon
          points="50,24 58,60 50,54 42,60"
          className="fill-current text-primary"
          transform={`rotate(${(direction + 180) % 360} 50 50)`}
        />
      )}
    </svg>
  );
}

export function WeatherContent({ condoSlug }) {
  const { weather, status } = useWeather(condoSlug);

  if (status === 'loading') {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (status === 'notFound') {
    return (
      <div className="text-center py-12">
        <h1 className="text-3xl font-bold text-foreground mb-4">Page Not Found</h1>
        <p className="text-muted-foreground">This page doesn't exist.</p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="bg-destructive/20 border border-destructive text-foreground px-4 py-3 rounded mt-6">
        Weather data is currently unavailable. Please try again later.
      </div>
    );
  }

  const { wind, rain } = weather;
  const uv = weather.uvi === null ? null : uvLevel(weather.uvi);
  const outdated = weather.stale || (weather.observedAt && Date.now() - new Date(weather.observedAt).getTime() > OUTDATED_AFTER_MS);

  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Weather</h1>
          <p className="text-sm text-muted-foreground">
            Live readings from the weather station located on the residence's premises.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {outdated && (
            <span className="px-2 py-0.5 rounded bg-priorityMedium/20 text-priorityMedium">Data may be outdated</span>
          )}
          {weather.observedAt && <span>Updated {formatAge(weather.observedAt)}</span>}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="md:col-span-2">
          <Card title="Outside">
            <p className="text-5xl font-bold text-foreground">{show(weather.temperature, 1)}°C</p>
            <div className="grid grid-cols-3 gap-4 mt-4">
              <Stat label="Feels like" value={show(weather.feelsLike, 1)} unit="°C" />
              <Stat label="Humidity" value={show(weather.humidity)} unit="%" />
              <Stat label="Dew point" value={show(weather.dewPoint, 1)} unit="°C" />
            </div>
          </Card>
        </div>

        <Card title="Wind">
          <div className="flex items-center gap-4">
            <Compass direction={wind.direction} />
            <div className="space-y-2">
              <Stat label="Speed" value={show(wind.speed, 1)} unit="km/h" />
              <Stat label="Gust" value={show(wind.gust, 1)} unit="km/h" />
              {wind.direction !== null && (
                <p className="text-sm text-muted-foreground">From {compassPoint(wind.direction)} ({show(wind.direction)}°)</p>
              )}
            </div>
          </div>
        </Card>

        <Card title="Rain">
          <p className="text-3xl font-bold text-foreground">
            {show(rain.daily, 1)}<span className="text-base font-normal text-muted-foreground"> mm today</span>
          </p>
          <p className="text-sm text-muted-foreground mb-3">Rate: {show(rain.rate, 1)} mm/h</p>
          <div className="grid grid-cols-3 gap-4">
            <Stat label="Last hour" value={show(rain.hourly, 1)} unit="mm" />
            <Stat label="24 hours" value={show(rain.last24h, 1)} unit="mm" />
            <Stat label="Event" value={show(rain.event, 1)} unit="mm" />
            <Stat label="Week" value={show(rain.weekly, 1)} unit="mm" />
            <Stat label="Month" value={show(rain.monthly, 1)} unit="mm" />
            <Stat label="Year" value={show(rain.yearly, 1)} unit="mm" />
          </div>
        </Card>

        <Card title="Pressure">
          <p className="text-3xl font-bold text-foreground">
            {show(weather.pressure, 1)}<span className="text-base font-normal text-muted-foreground"> hPa</span>
          </p>
        </Card>

        <Card title="Sun">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs text-muted-foreground">UV index</p>
              <p className="text-3xl font-bold text-foreground">{show(weather.uvi)}</p>
              {uv && <span className={`inline-block mt-1 px-2 py-0.5 rounded text-sm ${uv.className}`}>{uv.label}</span>}
            </div>
            <Stat label="Solar radiation" value={show(weather.solar)} unit="W/m²" />
          </div>
        </Card>
      </div>
    </div>
  );
}

function WeatherPage() {
  return (
    <CondoLayout>
      {({ condoSlug }) => <WeatherContent condoSlug={condoSlug} />}
    </CondoLayout>
  );
}

export default WeatherPage;
